import { ArrowRight, CaretRight, MagnifyingGlass } from "@phosphor-icons/react"
import { Button } from "../../../components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "../../../components/ui/table"
import { cn } from "../../../lib/utils"
import type { MatterCard, MatterCardsData } from "../types"
import type { ViewMode } from "../stores/global-ai-store"

interface GlobalAIMatterTableProps {
  matterCards: MatterCardsData
  onCardClick: (card: MatterCard) => void
  viewMode: ViewMode
  onToggleView: () => void
}

export function GlobalAIMatterTable({
  matterCards,
  onCardClick,
  viewMode,
  onToggleView,
}: GlobalAIMatterTableProps) {
  const { message, cards } = matterCards
  const hasResults = cards.length > 0

  return (
    <div
      className={cn(
        "h-full flex flex-col bg-background",
        "transition-all duration-200 ease-in-out",
        viewMode === "results" ? "flex-1" : "w-[390px]"
      )}
    >
      {/* Panel header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/50">
        <span className="text-xs font-medium text-muted-foreground">
          {hasResults
            ? `${cards.length} matter${cards.length !== 1 ? "s" : ""} found`
            : "No results found"}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={onToggleView}
          title={viewMode === "split" ? "Hide results" : "Show chat"}
        >
          <CaretRight className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Optional message */}
      {message && (
        <div className="px-3 py-2 text-xs text-muted-foreground border-b border-border">
          {message}
        </div>
      )}

      {/* No results state */}
      {!hasResults && (
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center text-muted-foreground">
            <MagnifyingGlass className="h-10 w-10 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No matters matched your search</p>
          </div>
        </div>
      )}

      {/* Table */}
      {hasResults && (
        <div className="flex-1 overflow-auto">
          <Table>
            <TableBody>
              {cards.map((card) => {
                const info = card.pending_action || card.subtitle
                return (
                  <TableRow
                    key={card.matter_id}
                    className="cursor-pointer group"
                    onClick={() => onCardClick(card)}
                  >
                    <TableCell className="py-2">
                      <div className="font-medium text-sm truncate">
                        {card.name}
                      </div>
                      {info && (
                        <div className="text-xs text-muted-foreground truncate mt-0.5">
                          {info}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="py-2 pr-2 align-middle">
                      <ArrowRight
                        className={cn(
                          "h-3.5 w-3.5 text-muted-foreground",
                          "opacity-0 group-hover:opacity-100",
                          "transition-opacity duration-150"
                        )}
                      />
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
